/**
 * Pure helper bridging the Sources panel (#20423 Story 4) to the annotated
 * document viewer (#20423 Story 3).
 *
 * Given the FULL provenance payload for a message and one document
 * (addressed by its source_data_record id), produce the list of
 * {@link DocumentAnnotation}s for that document: every Cited passage from it
 * (cited:true, with a citationNumber) plus every Read passage from it
 * (cited:false). Grouping by document means clicking any source surfaces all
 * of that document's annotations together.
 *
 * citationNumber stays consistent with the inline `[N]` markers and the
 * Sources panel: numbers are built through {@link mergeCitedWithUnavailable},
 * so a cited-but-unavailable document keeps its slot and numbering never
 * shifts when a document disappears (falls back to `index + 1` when there are
 * no unavailable entries — identical to the legacy numbering).
 */
import type {
  AssistantResponseSources,
  ProvenanceSource,
} from "../../types/provenance";
import { mergeCitedWithUnavailable } from "./citedTierMerge";
import type { SourceProvenanceReference } from "../../services/ArtifactService";
import { manifestPageTarget } from "../../utils/manifestPage";
import type {
  BBoxSpace,
  DocumentAnnotation,
} from "src/views/datasets/documentAnnotations";

/** Stable identity for a document across cited/read tiers. */
const recordKey = (source: ProvenanceSource): string => {
  const sdr = source.source_data_record;
  // Prefer the numeric id; fall back to source_uid so tabular/uploaded rows
  // that share the same id space still group sensibly.
  if (sdr?.id != null) return `id:${sdr.id}`;
  if (sdr?.source_uid) return `uid:${sdr.source_uid}`;
  return "";
};

/**
 * Resolve the page that carries the most of a chunk's content (by bounding-box
 * area). A chunk that spans a page break often starts on a cover/header page
 * whose only boxes are a title + print marks, with the real prose on the next
 * page; using ``page_number[0]`` would anchor the highlight/jump to that
 * cover/header page (#20423). Picking the densest page lands the reader on the
 * actual cited content. Falls back to ``page_number[0]`` then the first box's
 * page when no boxes carry area.
 */
export const resolveContentPage = (
  source: ProvenanceSource,
): number | undefined => {
  const cm = source.data_chunk?.chunk_metadata as
    | {
        page_number?: number[];
        bounding_boxes?: { page: number; width?: number; height?: number }[];
      }
    | undefined;
  const boxes = cm?.bounding_boxes;
  if (boxes && boxes.length > 0) {
    const areaByPage = new Map<number, number>();
    for (const b of boxes) {
      if (typeof b.page !== "number") continue;
      const area = (b.width ?? 0) * (b.height ?? 0);
      areaByPage.set(b.page, (areaByPage.get(b.page) ?? 0) + area);
    }
    let best: number | undefined;
    let bestArea = -1;
    for (const [page, area] of areaByPage) {
      if (area > bestArea) {
        bestArea = area;
        best = page;
      }
    }
    if (best != null) return best;
  }
  return cm?.page_number?.[0] ?? cm?.bounding_boxes?.[0]?.page;
};

/**
 * Resolve a tabular/CSV row highlight from chunk metadata, if the chunk carries
 * a stable row key. The CSV/XLSX chunker (backend `chunking_strategies.py`)
 * emits `start_row`/`end_row` as 1-based indices into the body (header
 * excluded). The tabular viewer's `row` is 0-indexed into the body, so we anchor
 * the highlight to the chunk's first body row: `row = start_row - 1`.
 *
 * A chunk spans a RANGE of rows but the viewer + store model a single `row`
 * (one highlight anchor per chunk), so we anchor to the start row only — the
 * viewer pages to it with a context window. Returns undefined when no stable row
 * key is present (e.g. non-tabular chunks), so PDF annotations stay row-less and
 * tabular chunks without a row key degrade to the section/page banner.
 */
const resolveRow = (source: ProvenanceSource): number | undefined => {
  const cm = source.data_chunk?.chunk_metadata as
    | { start_row?: number; chunk_type?: string }
    | undefined;
  const startRow = cm?.start_row;
  if (typeof startRow !== "number") {
    // No stable row key — do NOT fabricate one. A tabular chunk (chunk_type
    // "row_chunk", emitted by the CSV/XLSX chunker) without a row index can't
    // be row-highlighted; it degrades to the viewer's section/page banner. Log
    // the limitation so this isn't a silent dead path (contract §Annotations).
    if (cm?.chunk_type === "row_chunk") {
      console.debug(
        "[provenance] tabular chunk has no start_row; row highlight " +
          "unavailable — falling back to page/section banner",
        { chunk_id: source.data_chunk?.id },
      );
    }
    return undefined;
  }
  // 1-based body index -> 0-based viewer row. Clamp at 0 defensively.
  return Math.max(0, startRow - 1);
};

const toAnnotation = (
  source: ProvenanceSource,
  citationNumber?: number,
): DocumentAnnotation => {
  const cm = source.data_chunk?.chunk_metadata as
    | {
        bounding_boxes?: DocumentAnnotation["bounding_boxes"];
        bbox_space?: BBoxSpace;
      }
    | undefined;
  const page = resolveContentPage(source);
  const row = resolveRow(source);
  return {
    chunk_id: source.data_chunk.id,
    // The viewer's DocumentAnnotation requires a page; default to page 1 when
    // neither page_number nor bounding boxes carry one (page-level banner).
    page: page ?? 1,
    bounding_boxes: cm?.bounding_boxes,
    // Per-annotation coordinate space (#20423 Gap 8); omit when absent so the
    // viewer applies its default ("ocr_px").
    ...(cm?.bbox_space ? { bbox_space: cm.bbox_space } : {}),
    cited: source.cited,
    ...(citationNumber != null ? { citationNumber } : {}),
    // Tabular row highlight (#20423 Gap 11); omitted for PDF/non-tabular chunks.
    ...(row != null ? { row } : {}),
  };
};

/**
 * Build the annotation list for a single document from the whole provenance
 * payload.
 *
 * @param provenance - The full message provenance (cited + read tiers).
 * @param sourceDataRecordId - The source_data_record id of the clicked
 *   document. Accepts the numeric id or the source_uid string so callers can
 *   pass whichever they have.
 * @returns DocumentAnnotations for that document only (cited first, then read).
 */
export const buildDocumentAnnotations = (
  provenance: AssistantResponseSources | null | undefined,
  sourceDataRecordId: number | string,
): DocumentAnnotation[] => {
  if (!provenance) return [];

  const targetKey =
    typeof sourceDataRecordId === "number"
      ? `id:${sourceDataRecordId}`
      : // Match either the numeric id rendered as a string or a source_uid.
        sourceDataRecordId;

  const matches = (source: ProvenanceSource): boolean => {
    const sdr = source.source_data_record;
    if (
      sdr?.id != null &&
      (`id:${sdr.id}` === targetKey || `${sdr.id}` === targetKey)
    )
      return true;
    if (
      sdr?.source_uid &&
      (`uid:${sdr.source_uid}` === targetKey || sdr.source_uid === targetKey)
    )
      return true;
    return recordKey(source) === targetKey;
  };

  const cited = provenance.assistant_response_sources ?? [];
  const read = provenance.read_sources ?? [];

  const annotations: DocumentAnnotation[] = [];

  // Cited passages: gap-aware citationNumber via mergeCitedWithUnavailable
  // (mirrors the inline [N] markers and the Sources panel's numbering).
  const merged = mergeCitedWithUnavailable(
    cited,
    (provenance.unavailable_sources ?? []).filter((u) => u.cited),
  );
  for (const entry of merged) {
    if (entry.kind === "source" && matches(entry.source)) {
      annotations.push(toAnnotation(entry.source, entry.citationNumber));
    }
  }

  // Read-but-not-cited passages from the same document.
  read.forEach((source) => {
    if (matches(source)) {
      annotations.push(toAnnotation(source));
    }
  });

  return annotations;
};

/**
 * Build annotations for ONE document from a generated-document provenance
 * manifest (#20423 Story 5, "FROM [n]" deep-link).
 *
 * The manifest is a flat list of {@link SourceProvenanceReference} (one per
 * reference number `n`), unlike the chunk-backed message provenance consumed by
 * {@link buildDocumentAnnotations}. The manifest carries `chunk_id` + `page`
 * but no bounding boxes, so each annotation degrades to a page-level banner
 * (the viewer shows "Cited [n] on this page" when no bbox is present). Every
 * reference in a generated doc is a cited source, so annotations are marked
 * `cited: true` with `citationNumber = n`.
 *
 * @param references - All references from the artifact's manifest.
 * @param sourceDataRecordId - The SDR whose references should be highlighted
 *   (matches the document being opened). References for other documents are
 *   skipped, mirroring how {@link buildDocumentAnnotations} groups by document.
 * @returns DocumentAnnotations for that document only (empty when the SDR has
 *   no resolvable chunk id, leaving the overlay inert).
 */
export const buildManifestAnnotations = (
  references: SourceProvenanceReference[] | null | undefined,
  sourceDataRecordId: number,
): DocumentAnnotation[] => {
  if (!references?.length) return [];

  const annotations: DocumentAnnotation[] = [];
  references.forEach((ref) => {
    if (ref.source_data_record_id !== sourceDataRecordId) return;
    // Without a chunk id the viewer has nothing to anchor the highlight to.
    if (ref.chunk_id == null) return;
    annotations.push({
      chunk_id: ref.chunk_id,
      // Highlight placement compares `page` by equality, so a multi-page span
      // must collapse to its first page rather than carrying the raw array.
      page: manifestPageTarget(ref.page) ?? 1,
      cited: true,
      citationNumber: ref.n,
    });
  });

  return annotations;
};
